import { useState, useRef } from "react";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import { useGetBugDetailByBugIdQuery } from "@/features/bughurd/api";
import {
  Avatar,
  Box,
  ClickAwayListener,
  TextField,
  Tooltip,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import { DataGridPro, GridColDef, GridOverlay } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import {
  colorPalette,
  getInitials,
  getSeverityIcon,
  severityColorMap,
  typeToIconMapping,
} from "../utils";
import {
  CalendarToday,
  ChatBubbleOutline,
  PersonAddOutlined,
  Add,
} from "@mui/icons-material";
import moment from "moment";
import {
  useUpdateBugDetailMutation,
  useCreateBugMutation,
  useGetListOfStatusQuery,
} from "@/features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import EditPopover from "./EditPopover";
import { toast } from "react-toastify";
import TaskUpdateDrawer from "../bugherdComponents/TaskUpdateDrawer";

const ADD_SUBTASK_ROW_ID = "add-subtask-row";
const LINK_EPIC_TASK_ROW_ID = "link-epic-task-row";

const SubtaskTab = ({ bugId }: { bugId: string }) => {
  const { t } = useTranslation(["bugherd"]);
  const customLocaleText = useCustomLocaleText();
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // State for inline editing
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  // State for name editing
  const [editingNameId, setEditingNameId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const nameInputRef = useRef(null);

  // State for adding subtasks
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

  // API hooks
  const {
    data: bugDetailData,
    isLoading,
    refetch: refetchBugDetail,
    isFetching: isFetchingBugDetail,
  } = useGetBugDetailByBugIdQuery(
    { bugId, withDetails: true },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateBug, { isLoading: updatingBug }] = useUpdateBugDetailMutation();
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const { data: companyMemberData } = useGetCompanyMembersQuery(1);
  const { data: statusLists } = useGetListOfStatusQuery(
    { projectId: bugDetailData?.data?.decryptedData?.project?._id },
    { skip: !bugDetailData?.data?.decryptedData?.project?._id }
  );

  const users = companyMemberData?.data?.decryptedData || [];
  const availableStatus = statusLists?.data?.decryptedData?.items || [];
  const subTasks = bugDetailData?.data?.decryptedData?.subTasks || [];
  const epicTasks = bugDetailData?.data?.decryptedData?.epicTasks || [];

  const isEpic =
    bugDetailData?.data?.decryptedData?.type === "EPIC" || false;
  const tasksToShow = isEpic ? epicTasks : subTasks;

  const listData = isEpic
    ? tasksToShow
    : [...tasksToShow, { _id: ADD_SUBTASK_ROW_ID, summary: "" }];

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) {
      return;
    }

    try {
      const payload = {
        parentBug: bugId,
        summary: newSubtaskTitle,
        type: "BUG",
        project: bugDetailData?.data?.decryptedData?.project?._id,
        bugStatus: bugDetailData?.data?.decryptedData?.bugStatus?._id,
        severity: bugDetailData?.data?.decryptedData?.severity,
      };

      await createBug(payload).unwrap();
      refetchBugDetail();
      toast.success(t("Subtask created successfully"));
      setNewSubtaskTitle("");
      setIsAddingSubtask(false);
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error(t("Failed to create subtask"));
    }
  };

  const handleSubtaskKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleAddSubtask();
    } else if (e.key === "Escape") {
      setIsAddingSubtask(false);
      setNewSubtaskTitle("");
    }
  };

  const handleSubtaskTextChange = (e) => {
    setNewSubtaskTitle(e.target.value);
  };

  const handleNameClick = (event, taskId, currentName) => {
    if (taskId === ADD_SUBTASK_ROW_ID || taskId === LINK_EPIC_TASK_ROW_ID)
      return;

    event.stopPropagation();
    setEditingNameId(taskId);
    setEditedName(currentName);
  };

  const handleNameChange = (event) => {
    setEditedName(event.target.value);
  };

  const saveEditedName = async () => {
    if (!editedName.trim()) {
      toast.error(t("Name cannot be empty"));
      return;
    }

    try {
      const task = listData.find((task) => task._id === editingNameId);
      if (!task) {
        toast.error(t("Task not found"));
        return;
      }

      const updatePayload = {
        bugId: task._id,
        summary: editedName.trim(),
        description: task.description,
        severity: task.severity,
        assignTo: task.assignTo?.map((assignee) => assignee._id) || [],
        pageUrl: task.pageUrl,
        // updatedAt: new Date(),
        bugStatus: task.bugStatus?._id,
        screenShots: task.screenShots,
        attachments: task.attachments,
        tags: task.tags,
        // createdAt: task.createdAt,
        type: task.type,
        platform: task.platform,
        jiraConfig: {
          projectKey: task.jiraConfig?.projectKey,
          projectId: task.project?._id || task.project,
          projectName: task.projectName,
          jiraDatas: task.jiraConfig?.jiraDatas,
        },
        dueDate: task.dueDate,
        estimation: task.estimation,
        project: task.project?._id,
      };

      await updateBug(updatePayload)
        .unwrap()
        .then(() => {
          toast.success(t("Subtask name updated successfully"));
          refetchBugDetail();
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            t("Failed to update subtask name:", {
              error: error?.data?.message || t("Unknown error"),
            })
          );
        });
    } catch (error) {
      console.error("Error updating subtask name:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      setEditingNameId(null);
    }
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveEditedName();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEditingNameId(null);
    }
  };

  const handleClickEdit = async (event, taskId, field) => {
    if (taskId === ADD_SUBTASK_ROW_ID || taskId === LINK_EPIC_TASK_ROW_ID)
      return;

    event.stopPropagation();
    setEditingTaskId(taskId);
    setEditingField(field);
    setAnchorEl(event.currentTarget);

    if (field === "assignTo") {
      const task = listData.find((task) => task._id === taskId);
      if (task && task.assignTo) {
        setSelectedAssignees(
          users.filter((user) =>
            task.assignTo.some((assignee) => assignee._id === user._id)
          )
        );
      } else {
        setSelectedAssignees([]);
      }
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setEditingTaskId(null);
    setEditingField(null);
  };

  const handleAssigneeChange = (newAssignees) => {
    setSelectedAssignees(newAssignees);
    if (editingTaskId) {
      const assigneeIds = newAssignees.map((user) => user._id);
      handleUpdateField(assigneeIds);
    }
    handleClosePopover();
  };

  const handleUpdateField = async (value) => {
    try {
      const task = listData.find((task) => task._id === editingTaskId);

      if (!task) {
        toast.error(t("Task not found"));
        handleClosePopover();
        return;
      }

      const updatePayload = {
        bugId: task._id,
        summary: task.summary,
        description: task.description,
        severity: task.severity,
        assignTo: task.assignTo?.map((assignee) => assignee._id) || [],
        pageUrl: task.pageUrl,
        updatedAt: new Date(),
        bugStatus: task.bugStatus?._id,
        screenShots: task.screenShots,
        attachments: task.attachments,
        tags: task.tags,
        createdAt: task.createdAt,
        type: task.type,
        platform: task.platform,
        jiraConfig: {
          projectKey: task.jiraConfig?.projectKey,
          projectId: task.project?._id || task.project,
          projectName: task.project?.name || task.projectName,
          jiraDatas: task.jiraConfig?.jiraDatas || {},
        },
        dueDate: task.dueDate,
        estimation: task.estimation,
        project: task.project?._id || task.project,
      };

      switch (editingField) {
        case "severity":
          updatePayload.severity = value;
          break;
        case "bugStatus":
          updatePayload.bugStatus = value;
          break;
        case "dueDate":
          updatePayload.dueDate = value;
          break;
        case "assignTo":
          updatePayload.assignTo = value;
          break;
        default:
          break;
      }

      await updateBug(updatePayload)
        .unwrap()
        .then(() => {
          toast.success(
            `${
              editingField === "bugStatus"
                ? "Status"
                : editingField === "severity"
                ? "Priority"
                : editingField === "assignTo"
                ? "Assignee"
                : "Due Date"
            } ${t("updated successfully")}`
          );
          refetchBugDetail();
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            `Failed to update ${
              editingField === "bugStatus"
                ? "status"
                : editingField === "severity"
                ? "priority"
                : editingField === "assignTo"
                ? "assignee"
                : "due date"
            }: ${error?.data?.message || "Unknown error"}`
          );
        });
    } catch (error) {
      console.error("Error updating subtask:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      handleClosePopover();
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
  };

  const columns: GridColDef[] = [
    {
      field: "summary",
      headerName: t("Name"),
      flex: 1.5,
      renderCell: (params) => {
        const rowType = params?.row?.type || "";
        const typeInfo = rowType
          ? typeToIconMapping[rowType]
          : typeToIconMapping.TASK;

        if (params.id === ADD_SUBTASK_ROW_ID) {
          if (!isAddingSubtask) {
            return (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddingSubtask(true);
                }}
                sx={{
                  height: "fit-content",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  color: "#6B7888",
                  textTransform: "none",
                  fontSize: "13px",
                  minHeight: "27px",
                  p: 1,
                  justifyContent: "flex-start",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                  },
                }}
                startIcon={<Add />}
              >
                {t("Add subtask")}
              </Button>
            );
          } else {
            return (
              <ClickAwayListener
                onClickAway={() => {
                  setIsAddingSubtask(false);
                  setNewSubtaskTitle("");
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 1,
                    p: 1,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TextField
                    size="small"
                    value={newSubtaskTitle}
                    onChange={handleSubtaskTextChange}
                    onKeyDown={handleSubtaskKeyDown}
                    placeholder={t("Enter subtask name...")}
                    autoFocus
                    inputProps={{
                      onKeyDown: (e) => e.stopPropagation(),
                      onClick: (e) => e.stopPropagation(),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                      "& .MuiInputBase-input": {
                        px: 1,
                        py: 0.5,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddSubtask();
                      }}
                    >
                      {t("Add")}
                    </Button>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddingSubtask(false);
                        setNewSubtaskTitle("");
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            );
          }
        }

        const isEditing = editingNameId === params.row._id;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              cursor: "pointer",
              position: "relative",
              overflow: "visible",
              height: "100%",
            }}
          >
            {isEditing ? (
              <ClickAwayListener onClickAway={saveEditedName}>
                <TextField
                  size="small"
                  value={editedName}
                  onChange={handleNameChange}
                  inputRef={nameInputRef}
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                    "& .MuiInputBase-input": {
                      px: 1,
                      py: 0.5,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleNameKeyDown(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { fontSize: "0.875rem", fontWeight: 500 },
                  }}
                />
              </ClickAwayListener>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                {rowType && (
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo?.bgcolor,
                      borderRadius: "3px",
                      color: "white",
                    }}
                  >
                    {typeInfo?.icon}
                  </Box>
                )}
                {params?.row?.bugKey && (
                  <span
                    style={{
                      marginRight: "4px",
                      color: "#1976D2",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsModalOpen(true);
                      setSelectedTaskId(params?.row?._id);
                    }}
                  >
                    {params?.row?.bugKey}:
                  </span>
                )}
                <Typography
                  title={params?.value?.length > 30 ? params?.value : ""}
                  onClick={(e) => {
                    if (
                      !isEditing &&
                      params.id !== ADD_SUBTASK_ROW_ID &&
                      params.id !== LINK_EPIC_TASK_ROW_ID
                    ) {
                      handleNameClick(e, params.row._id, params.value);
                    }
                  }}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  {params?.value}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "assignTo",
      headerName: t("Assignee"),
      flex: 0.7,
      sortComparator: (v1, v2) => {
        if (!v1 || v1.length === 0) {
          if (!v2 || v2.length === 0) return 0;
          return 1;
        }
        if (!v2 || v2.length === 0) return -1;

        const name1 = v1[0]?.fullName?.toLowerCase() || "";
        const name2 = v2[0]?.fullName?.toLowerCase() || "";

        return name1.localeCompare(name2);
      },
      renderCell: (params) => {
        if (
          params.id === ADD_SUBTASK_ROW_ID ||
          params.id === LINK_EPIC_TASK_ROW_ID
        )
          return null;

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              height: "100%",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "assignTo")}
          >
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {params?.formattedValue?.length === 0 && (
                <PersonAddOutlined
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
              )}
              {params?.formattedValue?.slice(0, 3)?.map((item, index) => (
                <Tooltip
                  id="snr-ai-userList-tooltip"
                  key={item._id}
                  title={item.fullName}
                  arrow
                >
                  <Avatar
                    key={item._id}
                    sx={{
                      backgroundColor:
                        colorPalette[index % colorPalette.length],
                      marginRight: "-4px",
                      cursor: "pointer",
                      position: "relative",
                      zIndex: 10 - index,
                      width: "25px",
                      height: "25px",
                      fontSize: "10px",
                      fontWeight: 500,
                      border: "1px solid white",
                    }}
                  >
                    {getInitials(item?.fullName)}
                  </Avatar>
                </Tooltip>
              ))}

              {params?.formattedValue?.length > 3 && (
                <Avatar
                  sx={{
                    backgroundColor: "#ccc",
                    marginRight: 1,
                    cursor: "pointer",
                    width: "30px",
                    height: "30px",
                    fontSize: "12px",
                    border: "1px solid #fff",
                    fontWeight: 500,
                  }}
                >
                  +{params?.formattedValue?.length - 3}
                </Avatar>
              )}
            </Box>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "dueDate",
      headerName: t("Due Date"),
      flex: 0.7,
      renderCell: (params) => {
        if (
          params.id === ADD_SUBTASK_ROW_ID ||
          params.id === LINK_EPIC_TASK_ROW_ID
        )
          return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "dueDate")}
          >
            {params?.row?.dueDate ? (
              <Typography variant="body2">
                {params?.row?.dueDate &&
                  moment(
                    params?.row?.dueDate,
                    "YYYY-MM-DDTHH:mm:ss:SSS"
                  )?.isValid() &&
                  moment(
                    params?.row?.dueDate,
                    "YYYY-MM-DDTHH:mm:ss:SSS"
                  )?.format("DD MMM, YYYY")}
              </Typography>
            ) : (
              <CalendarToday
                sx={{ fontSize: "small", color: "text.secondary" }}
              />
            )}
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "severity",
      headerName: t("Priority"),
      flex: 0.7,
      renderCell: (params) => {
        if (
          params.id === ADD_SUBTASK_ROW_ID ||
          params.id === LINK_EPIC_TASK_ROW_ID
        )
          return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
              gap: 1,
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "severity")}
          >
            {getSeverityIcon(params?.row?.severity)}
            <Typography
              variant="body2"
              color={severityColorMap[params?.row?.severity] || "transparent"}
            >
              {params?.row?.severity ?? "-"}
            </Typography>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "bugStatus",
      headerName: t("Status"),
      flex: 0.7,
      renderCell: (params) => {
        if (
          params.id === ADD_SUBTASK_ROW_ID ||
          params.id === LINK_EPIC_TASK_ROW_ID
        )
          return null;

        const statusValue = isEpic
          ? Array.isArray(params?.row?.bugStatus)
            ? params?.row?.bugStatus[0]?.status
            : params?.row?.bugStatus?.status
          : params?.row?.bugStatus?.status;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "bugStatus")}
          >
            <Typography variant="body2">{statusValue ?? "-"}</Typography>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "commentCount",
      headerName: t("Comments"),
      flex: 0.4,
      renderCell: (params) => {
        if (
          params.id === ADD_SUBTASK_ROW_ID ||
          params.id === LINK_EPIC_TASK_ROW_ID
        )
          return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
              justifyContent: "center",
            }}
          >
            <ChatBubbleOutline sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ lineHeight: 1 }}>
              {params?.row?.commentCount || 0}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const editingTask =
    listData.find((task) => task._id === editingTaskId) || null;

  return (
    <Box>
      {/* Header - Hidden when used in Accordion */}
      {/* <Typography
        variant="h6"
        sx={{ mb: 2, color: "text.secondary", fontWeight: "bold" }}
      >
        {isEpic ? t("Associated Stories/Tasks") : t("Sub Tasks")}
      </Typography> */}
      <DataGridPro
        getRowId={(row) => row?._id}
        columns={columns}
        rows={listData}
        loading={
          isLoading || isFetchingBugDetail || updatingBug || isCreatingBug
        }
        rowSelection={false}
        localeText={customLocaleText}
        hideFooter={true}
        disableColumnMenu
        style={{
          border: "none",
          height: "auto",
        }}
        pagination={false}
        slots={{
          noRowsOverlay: () => (
            <GridOverlay>
              <div>
                {isEpic ? t("No Associated Stories/Tasks") : t("No Subtasks")}
              </div>
            </GridOverlay>
          ),
          pagination: () => null,
        }}
      />

      <EditPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        fieldType={editingField}
        editingTask={editingTask}
        onUpdate={handleUpdateField}
        theme={theme}
        t={t}
        severityOptions={[
          { value: "Highest", label: t("Highest"), icon: null },
          { value: "High", label: t("High"), icon: null },
          { value: "Medium", label: t("Medium"), icon: null },
          { value: "Low", label: t("Low"), icon: null },
        ]}
        statusOptions={availableStatus}
        isLoadingStatusLabel={false}
        users={users}
        selectedAssignees={selectedAssignees}
        handleAssigneeChange={handleAssigneeChange}
        handleAssigneeBlur={() => {}}
        isUpdating={updatingBug}
        typeOptions={[]}
      />
      <TaskUpdateDrawer
        open={isModalOpen}
        onClose={handleClose}
        setBugId={() => {}}
        setDrawerOpen={setIsModalOpen}
        bugId={selectedTaskId || ""}
        users={users || []}
        createdBy={""}
        setCreatedBy={() => {}}
        refreshColumn={() => {}}
      />
    </Box>
  );
};

export default SubtaskTab;
